import { CtaStrip, FeatureList, MarketingPage, Pillars } from '../../components/MarketingPage';

/**
 * Four segment-level landing pages, per business/strategy/WHITESPACE_STRATEGY.md.
 *
 * Each lives at /for-<segment>. Targets a buyer who self-selects by
 * business type from the homepage segment cards.
 */

export function ForAuctionHouses() {
  return (
    <MarketingPage
      seoTitle="Auction house software — AuctiSync for traditional auctioneers"
      seoDescription="Modern auction-house software for estate, antique, collectibles, and charity auctioneers. Persistent inventory, consignor statements, staggered close, print catalog."
      canonicalPath="/for-auction-houses"
      eyebrow="For auction houses"
      headline="Every auction a DTA runs, AuctiSync runs."
      subhead="Full-service auction house software — estate, antique, collectibles, charity. The bidding engine, consignor statements, print catalogs, and staggered close you expect, with a modern interface that doesn't look 1998."
    >
      <Pillars
        title="Built for real auctioneers, not just bid-count dashboards"
        items={[
          {
            title: 'Persistent inventory',
            body: 'Items exist independently of auctions. Sell today, relist tomorrow, track quantity-on-hand across the yard.',
          },
          {
            title: 'Consignor statements + payouts',
            body: 'Commission per item, expense tracking, automated statements, Stripe Connect payouts. No spreadsheets.',
          },
          {
            title: 'Staggered close + anti-snipe',
            body: 'Configurable lot-groups, extension thresholds, reset vs extend modes. Server-authoritative timer.',
          },
          {
            title: 'Print catalog generator',
            body: 'Mailing catalogs for the buyers who still want paper. PDF-ready, fully branded.',
          },
          {
            title: 'Bidder approval + CC pre-auth',
            body: 'Approve before bid. Pre-authorize credit cards up-front. Shill detection on every session.',
          },
          {
            title: 'Named bid increment scales',
            body: "The detail that's missing from competing platforms. Named scales, reusable across auctions.",
          },
        ]}
      />

      <FeatureList
        title="Sub-niches welcome"
        body="Four dedicated landing pages with the vocabulary, keywords, and SEO for your specific auction type."
        bullets={[
          'Estate auction software',
          'Antique & vintage auction software',
          'Collectibles auction software (sports cards, coins, comics, stamps)',
          'Charity / fundraiser auction software',
          'Live + online bidding',
          'Consignor statements',
          'Print catalog',
          'Mobile cataloging app',
        ]}
      />

      <CtaStrip
        headline="Run your next auction on something built after 2005."
        subhead="See how DTA moved 10+ years of legacy weekly auctions to AuctiSync."
        primaryLabel="Read the DTA case study"
        primaryHref="/case-studies/dta"
        secondaryLabel="See pricing"
        secondaryHref="/pricing"
      />
    </MarketingPage>
  );
}

export function ForFacilities() {
  return (
    <MarketingPage
      seoTitle="Lien-law auction software — AuctiSync for facilities"
      seoDescription="Storage units, auto repair, RV, tow yards, marina, pawn shops. State-specific lien compliance engine with delinquency-to-auction workflow in one platform."
      canonicalPath="/for-facilities"
      eyebrow="For facilities"
      headline="From delinquent to sold — compliant in every state you operate."
      subhead="Self-storage, auto repair, RV, tow, marina, pawn. The lien-law compliance engine the incumbents refused to build. State rules, notice delivery, advertisement placement, redemption tracking — all in one platform."
    >
      <Pillars
        title="One engine, every lien-law vertical"
        items={[
          {
            title: 'State-specific rule engine',
            body: "Launching in FL, TX, AZ, GA, NC — with each state's exact delinquency timers, notice requirements, and advertisement windows.",
          },
          {
            title: 'Certified notice delivery',
            body: 'RPost certified email + USPS Certified Mail API. Delivery proofs auto-attached to the audit PDF.',
          },
          {
            title: 'Column.us ad placement',
            body: "Required newspaper advertisements placed automatically through Column's API. No more manually calling the paper.",
          },
          {
            title: 'Redemption tracking',
            body: 'If the tenant pays late, the auction auto-pauses and the sale is cleanly voided. Never oversell a redeemed unit.',
          },
          {
            title: 'Auto-compliance gate',
            body: "A listing can't go live until every state-required timer has cleared. You literally cannot make a non-compliant sale.",
          },
          {
            title: 'Excess proceeds handling',
            body: 'State-specific excess proceeds accounting with full audit trail and automated tenant notifications.',
          },
        ]}
      />

      <FeatureList
        title="Purpose-built for each vertical"
        body="Same engine, vertical-specific forms and flows."
        bullets={[
          'Storage unit auctions (FL/TX/AZ/GA/NC at launch)',
          "Auto-repair mechanic's lien (Phase 1.5)",
          'RV + camper abandonment (Phase 1.5)',
          'Tow-yard abandoned vehicles (Phase 2)',
          'Trucking freight liens (Phase 2)',
          'Pawn-shop lien sales (Phase 2)',
          'Marina / boat liens (Phase 3)',
          'PMS integrations: Hummingbird, ESS, Syrasoft, CSV',
        ]}
      />

      <CtaStrip
        headline="Compliance you can prove, in the states you operate."
        subhead="Purpose-built lien-law workflow for storage, auto, and marina operators."
        primaryLabel="See Storage tier pricing"
        primaryHref="/pricing"
        secondaryLabel="Contact us"
        secondaryHref="/contact"
      />
    </MarketingPage>
  );
}

export function ForLiquidators() {
  return (
    <MarketingPage
      seoTitle="Business liquidation auction software — AuctiSync for liquidators"
      seoDescription="Industrial, restaurant, construction, office, bankruptcy. Trustee-Grade Add-On with court report generation, sealed bid ballroom, and chain-of-custody audit."
      canonicalPath="/for-liquidators"
      eyebrow="For liquidators"
      headline="From court-ordered to sold — with an audit trail that holds up."
      subhead="Business liquidation, industrial disposition, restaurant close-outs, Chapter 7/11 bankruptcy. Trustee-Grade Add-On bundles sealed bid ballroom, stalking horse workflow, court reports, and 7-year retention."
    >
      <Pillars
        title="Trustee-Grade infrastructure, free on Enterprise"
        items={[
          {
            title: 'Sealed bid ballroom',
            body: 'Multiple qualified bidders submit sealed bids simultaneously; opened at the court hearing with full audit trail.',
          },
          {
            title: 'Stalking horse workflow',
            body: 'Initial bidder purchase agreement with break-up fee protection. Higher-and-better mechanics for competing qualified bidders.',
          },
          {
            title: 'Court report generator',
            body: '§363 sale hearing PDF with complete bidding record, creditor notices, and chain-of-custody log.',
          },
          {
            title: '§363(f) Free and Clear docs',
            body: 'Buyer protection documentation for good-faith purchaser status. The paperwork trustees actually need.',
          },
          {
            title: '7-year retention',
            body: 'Federal bankruptcy record retention automatic. Nothing deleted without policy sign-off.',
          },
          {
            title: 'Chain-of-custody audit',
            body: 'Forensic-grade asset movement logging. Hash-chained, optionally anchored to public blockchain for integrity proof.',
          },
        ]}
      />

      <FeatureList
        title="Coverage across the liquidation landscape"
        bullets={[
          'Industrial equipment auctions',
          'Restaurant close-outs',
          'Construction equipment',
          'Office furniture',
          'Chapter 7/11 bankruptcy §363 sales',
          'Trustee-Grade Certified badge',
          'Approved auctioneer directory',
          'Creditor notification system',
        ]}
      />

      <CtaStrip
        headline="Run a court-ordered sale your US Trustee will sign off on."
        subhead="Trustee-Grade Add-On is $299/mo on Growth+, included on Enterprise."
        primaryLabel="See Trustee-Grade features"
        primaryHref="/features/lien-law-compliance"
        secondaryLabel="Pricing"
        secondaryHref="/pricing"
      />
    </MarketingPage>
  );
}

export function ForSpecialty() {
  return (
    <MarketingPage
      seoTitle="Specialty auction software — AuctiSync for live commerce and collectibles"
      seoDescription="TCG, watches, memorabilia, movie props, vintage. Live webcast, modern marketplace UX, niche field templates, mobile-first bidding."
      canonicalPath="/for-specialty"
      eyebrow="For specialty dealers"
      headline="Live commerce energy. Serious auction discipline."
      subhead="TCG, watches, music memorabilia, movie props, vintage. Whatnot-style mobile UX wrapped around a real bidding engine — with the niche-field templates your category actually needs."
    >
      <Pillars
        title="Built for the modern collectible marketplace"
        items={[
          {
            title: 'Live webcast + simulcast',
            body: 'Broadcast the final 30 minutes of each auction. Pro tier includes it; Elite gets unlimited.',
          },
          {
            title: 'Modern marketplace UX',
            body: 'Threaded comments, follow, color-coded urgency, fun facts, badges, smart alerts, swipe-browse. What Whatnot taught the market.',
          },
          {
            title: 'Niche field templates',
            body: 'TCG grades, watch reference numbers, memorabilia provenance, COA attachments. Each category has a form template.',
          },
          {
            title: 'Mobile-first bidding app',
            body: 'Capacitor-wrapped iOS + Android app. Push notifications, bid sounds, one-tap bidding.',
          },
          {
            title: 'Make Offer + Private Treaty',
            body: 'Post-auction recovery for unsold lots. Consignor-approved offer thresholds. Direct message flow.',
          },
          {
            title: 'Public ratings + seller trust',
            body: "Buyers rate sellers, sellers rate buyers. A reputation layer that Whatnot runs and AuctionFlex doesn't.",
          },
        ]}
      />

      <FeatureList
        title="Sub-niches ready to capture"
        bullets={[
          'Trading card game (TCG) auctions',
          'Watch auctions',
          'Music memorabilia',
          'Movie props + film memorabilia',
          'Vintage clothing, denim, toys',
          'Live webcast + chat',
          'Scheduled preview shows',
          'Smart alerts + follow-a-seller',
        ]}
      />

      <CtaStrip
        headline="Ship your collectible marketplace without building infrastructure from scratch."
        subhead="Live webcast included on Pro+. Niche field framework shipping Phase 1."
        primaryLabel="See Pro tier"
        primaryHref="/pricing"
        secondaryLabel="See the bidding engine"
        secondaryHref="/features/bidding-engine"
      />
    </MarketingPage>
  );
}
